
import {
  getBSSReport,
  getInsurances,
  getIPANumbers
} from '@/components/insurances/insurance.service'
import {
  getInsuranceSupervisorInsurances,
  getUserInsurances
} from '@/components/doctors/doctors.service'
import { InsuranceIPANumber, PaginationVM, Role, SelectVM } from '@/models'
import { Component, Vue, Watch } from 'vue-property-decorator'
import {
  getCurrentUser,
  ensureCurrentUserInsurances,
  getHeadDoctor,
  getUserRole,
  getUserRmtId
} from '../admin/users/users.service'
import moment from 'moment'
import xlsx from 'json-as-xlsx'
import Multiselect from 'vue-multiselect'
@Component({
  components: {
    Multiselect
  }
})
export default class InsuranceBSS extends Vue {
  public userRole = getUserRole()
  public insurances: SelectVM[] = []
  public selectedInsurances: SelectVM[] = []
  public selectedIPANumbers: SelectVM[] = []
  public ipaNumbers: SelectVM[] = []
  public ipaNumberList = [] as InsuranceIPANumber[]
  public supervisorAssignedIPAs: string[] = []

  public fields = [
    {
      key: 'index'
    },
    {
      key: 'documentID',
      label: 'NPI',
      sortable: true,
      order: 1
    },
    {
      key: 'doctor',
      label: 'Doctor Name',
      sortable: true,
      order: 2
    },
    {
      key: 'ipaNumber',
      label: 'IPA Number',
      sortable: true,
      order: 2
    },
    {
      key: 'speciality',
      label: 'Speciality',
      sortable: true,
      order: 3
    },
    {
      key: 'insuranceName',
      label: 'Insurance Name',
      sortable: true,
      order: 4
    },
    {
      key: 'patientActives',
      label: 'Active Patients',
      sortable: true,
      order: 5
    },
    {
      key: 'rafScore',
      label: 'Estimated Overall Risk',
      sortable: true,
      order: 6
    },

    {
      key: 'percentage',
      label: 'Percentage',
      sortable: true,
      order: 14
    }
  ]
  public currentUser = ''
  public items = []
  public perPage = 10
  public pageOptions = [10, 20, 50, 100, 250, 500, 1000]
  public pagination = {} as PaginationVM
  public searchQuery = ''
  public showSearch = false
  public showDismissibleAlert = true
  private async created() {
    this.userRole = getUserRole()
    this.currentUser = getCurrentUser()
    if (this.userRole == Role.Doctor || this.userRole == Role.Staff) {
      this.fields = this.fields.filter(
        c => c.key != 'doctor' && c.key != 'speciality' && c.key != 'documentID'
      )
      this.showSearch = false
    } else if (this.userRole == Role.DoctorStaff) {
      this.fields = this.fields.filter(
        c => c.key != 'doctor' && c.key != 'speciality' && c.key != 'documentID'
      )
      this.currentUser = getHeadDoctor()
      this.currentUser.userName = this.currentUser.documentId
    } else if (
      this.userRole === Role.Insurance ||
      this.userRole === Role.InsuranceSupervisor
    ) {
      // Keep Doctor Name (same as Patients list for these roles).
      this.showSearch = true
    } else {
      this.showSearch = true
    }
    await this.loadReportFilters()
    await this._getBSSReport()
  }
  get showInsuranceFilter() {
    return (
      this.userRole === Role.Insurance ||
      this.userRole === Role.InsuranceStaff ||
      this.userRole === Role.InsuranceSupervisor ||
      this.userRole === Role.Admin ||
      this.userRole === Role.Superuser
    )
  }
  get showIpaFilter() {
    return (
      this.userRole === Role.Insurance ||
      this.userRole === Role.InsuranceSupervisor ||
      this.userRole === Role.Admin ||
      this.userRole === Role.Superuser
    )
  }
  @Watch('perPage')
  perPageChange() {
    this._getBSSReport()
  }
  get _pagination() {
    return this.pagination
  }
  public async onPageChange(pageNumber: number) {
    await this._getBSSReport(pageNumber, this.searchQuery)
  }
  public async cleanFilter() {
    this.searchQuery = ''
    this.selectedInsurances = []
    this.selectedIPANumbers = []
    this.refreshIpaOptions()
    await this._getBSSReport(1, this.searchQuery)
  }
  public async filter() {
    await this._getBSSReport(1, this.searchQuery)
  }
  @Watch('selectedInsurances', { deep: true })
  selectedInsurancesChange() {
    this.selectedIPANumbers = []
    this.refreshIpaOptions()
  }
  private getInsuranceCsv(): string {
    return [
      ...new Set(
        (this.selectedInsurances || [])
          .map(i => String(i.value).trim())
          .filter(Boolean)
      )
    ].join(',')
  }
  private getIpaCsv(): string {
    return [
      ...new Set(
        (this.selectedIPANumbers || [])
          .map(i => String(i.value).trim())
          .filter(Boolean)
      )
    ].join(',')
  }
  private refreshIpaOptions() {
    if (!this.showIpaFilter) {
      this.ipaNumbers = []
      return
    }
    let scopeIds = this.getInsuranceCsv()
      .split(',')
      .map(x => x.trim())
      .filter(Boolean)
    if (!scopeIds.length && this.insurances?.length) {
      scopeIds = [
        ...new Set(this.insurances.map(i => String(i.value)).filter(Boolean))
      ]
    }
    const assignedIpa = new Set(this.supervisorAssignedIPAs || [])
    this.ipaNumbers = (this.ipaNumberList || [])
      .filter(c => {
        const inScope =
          scopeIds.length === 0 || scopeIds.includes(String(c.insuranceID))
        const assignedOk =
          assignedIpa.size === 0 || assignedIpa.has(c.insuranceIPA)
        return inScope && assignedOk
      })
      .map(c => ({
        label: c.insuranceIPA,
        value: c.insuranceIPA
      }))
  }
  private async loadReportFilters() {
    if (!this.showInsuranceFilter) return
    const insuranceResponse = await getInsurances(1, 1000)
    const catalog = (insuranceResponse?.data || []).map(c => ({
      label: c.insuranceName,
      value: c.insuranceID
    }))
    if (
      this.userRole === Role.Insurance ||
      this.userRole === Role.InsuranceStaff
    ) {
      await ensureCurrentUserInsurances()
      const rmtUserId = Number(getUserRmtId())
      const assigned = rmtUserId ? await getUserInsurances(rmtUserId) : []
      const idSet = new Set(
        (assigned || []).map(a => String(a.insuranceID)).filter(Boolean)
      )
      this.insurances = catalog.filter(c => idSet.has(String(c.value)))
    } else if (this.userRole === Role.InsuranceSupervisor) {
      const rmtUserId = Number(getUserRmtId())
      const assigned = rmtUserId
        ? await getInsuranceSupervisorInsurances(rmtUserId)
        : []
      const idSet = new Set(
        (assigned || []).map(a => String(a.insuranceID)).filter(Boolean)
      )
      this.supervisorAssignedIPAs = [
        ...new Set(
          (assigned || []).map(a => a.insuranceIPA).filter(Boolean) as string[]
        )
      ]
      this.insurances = catalog.filter(c => idSet.has(String(c.value)))
    } else {
      this.insurances = catalog
    }
    if (this.showIpaFilter) {
      const ipaNumbersResponse = await getIPANumbers()
      this.ipaNumberList = ipaNumbersResponse?.data || []
      this.refreshIpaOptions()
    }
  }
  public async _getBSSReport(pageNumber = 1, searchQuery = '') {
    const res = await getBSSReport(
      pageNumber,
      this.perPage,
      searchQuery,
      this.getIpaCsv(),
      this.getInsuranceCsv()
    )
    this.items = Array.isArray(res?.data) ? res.data : []
    const paginationHeader = res?.headers?.['x-pagination']
    this.pagination = paginationHeader
      ? JSON.parse(paginationHeader)
      : ({} as PaginationVM)
  }
  public async exportTable() {
    let items = []
    const response = await getBSSReport(
      1,
      999999,
      this.searchQuery,
      this.getIpaCsv(),
      this.getInsuranceCsv()
    )
    items = response.data

    // console.log(items)
    const updatedArray = items.map(obj => {
      const newObj = {}
      // Sort the first array based on the order property
      this.fields.sort((a, b) => a.order - b.order)
      // Get the keys in the order specified by the first array
      const keys = this.fields.map(x => x.key).filter(c => c != 'index')
      // Sort the keys of the object
      keys.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const label = this.fields.find(x => x.key === key).label
          newObj[label.toLowerCase()] = obj[key.toLowerCase()]
        }
      })
      return newObj
    })
    // console.log(updatedArray)
    const data = [
      {
        sheet: 'Insurance 2nd Semester Report',
        columns: [],
        content: []
      }
    ]

    data[0].columns = this.fields
      .map(c => {
        return {
          label: c.label,
          value: c.key
        }
      })
      .filter(c => c.value != 'index')
    if (getUserRole() === Role.Doctor) {
      // alert(1)
      data[0].columns = [
        { label: 'NPI', value: 'documentID' },
        { label: 'Doctor Name', value: 'doctor' }
      ].concat(data[0].columns)
    }
    data[0].content = items.map(c => {
      return {
        ...c,
        valueOfPremium: '$' + c.valueOfPremium.toFixed(2),
        expensesOverall: this.formatMoney(c.expensesOverall),
        surplusAgreement: (c.surplusAgreement * 100).toFixed(2) + '%',
        annualBudget: this.formatMoney(c.annualBudget),
        mrl: (c.mrl * 100).toFixed(2) + '%',
        surplusTotal: this.formatMoney(c.surplusTotal),
        netSurplusBilledSsm: this.formatMoney(c.netSurplusBilledSsm),
        percentage: (c.percentage * 100).toFixed(2) + '%'
      }
    })

    const settings = {
      fileName: 'Insurance 2nd Semester Report', // Name of the resulting spreadsheet
      extraLength: 3, // A bigger number means that columns will be wider
      writeMode: 'writeFile', // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
      writeOptions: {}, // Style options from https://docs.sheetjs.com/docs/api/write-options
      RTL: false // Display the columns from right-to-left (the default value is false)
    }
    xlsx(data, settings) // Will download the excel file
  }
  public formatMoney(value) {
    return new Intl.NumberFormat('us-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value)
  }
  public formatDate(value: string) {
    if (value) return moment(value).format('MM/DD/YYYY')
  }
}
